import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Home, AlertCircle, CheckCircle, Download } from 'lucide-react';

// 导入工具类和配置
import formDataStorage from './utils/formDataStorage';
import { autoSaveCurrentFormData, getSpecificForm } from './services/formManagementService';
import { formPages, getFormPagesForVisaType } from './utils/formConfig';
import { getAllFormStatuses, formValidators } from './utils/formValidation';
import { canSubmit } from './utils/dataSubmitter';

// 导入共享组件
import ProgressBar from './components/shared/ProgressBar';
import Sidebar from './components/shared/Sidebar';
import UserCenter from './components/shared/UserCenter';
import FormInstructions from './components/shared/FormInstructions';

// 导入表单组件
import Personal1Form from './components/forms/Personal1Form';
import Personal2Form from './components/forms/Personal2Form';
import TravelForm from './components/forms/TravelForm';
import TravelCompanionsInfoForm from './components/forms/TravelCompanionsInfoForm';
import PreviousUSTravelInfoForm from './components/forms/PreviousUSTravelInfoForm';
import AddressPhoneForm from './components/forms/AddressPhoneForm';
import PassportVisaForm from './components/forms/PassportVisaForm';
import USContactForm from './components/forms/USContactForm';
import FamilyInfoForm from './components/forms/FamilyInfoForm';
import WorkEducationForm from './components/forms/WorkEducationForm';
import WorkEducation2Form from './components/forms/WorkEducation2Form';
import WorkEducation3Form from './components/forms/WorkEducation3Form';
import Part1Form from './components/forms/Part1Form';
import Part2Form from './components/forms/Part2Form';
import Part3Form from './components/forms/Part3Form';
import Part4Form from './components/forms/Part4Form';
import Part5Form from './components/forms/Part5Form';
import F1AdditionalContactsForm from './components/forms/F1AdditionalContactsForm';
import F1SchoolInfoForm from './components/forms/F1SchoolInfoForm';
import F2SevisInfoForm from './components/forms/F2SevisInfoForm';

// NOTE:表单组件映射
const formComponents = {
  personal_1: Personal1Form,
  personal_2: Personal2Form,
  travel: TravelForm,
  travel_companions_info: TravelCompanionsInfoForm,
  previous_US_travel_info: PreviousUSTravelInfoForm,
  address_phone: AddressPhoneForm,
  passport_visa: PassportVisaForm,
  us_contact: USContactForm,
  family_info: FamilyInfoForm,
  work_education: WorkEducationForm,
  work_education2: WorkEducation2Form,
  work_education3: WorkEducation3Form,
  part1: Part1Form,
  part2: Part2Form,
  part3: Part3Form,
  part4: Part4Form,
  part5: Part5Form,
  f1_school_info: F1SchoolInfoForm,
  f1_additional_contacts: F1AdditionalContactsForm,
  f2_sevis_info: F2SevisInfoForm
};

// 表单构建器组件
const FormBuilder = ({ applicationId, onLogout, onHistoryClick }) => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [currentPage, setCurrentPage] = useState('personal_1');
  const [formData, setFormData] = useState({});
  const [formStatuses, setFormStatuses] = useState({});
  const [submissionResult] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [dynamicFormPages, setDynamicFormPages] = useState(formPages);
  const [dynamicApplicationId, setDynamicApplicationId] = useState(applicationId || '');
  const [confirmationSnapshot, setConfirmationSnapshot] = useState(null);
  const [currentErrors, setCurrentErrors] = useState([]); // messages
  const [currentErrorDetails, setCurrentErrorDetails] = useState([]); // [{message, anchor}]
  const formTopRef = React.useRef(null);

  // 从formDataStorage加载已存在的application_id
  useEffect(() => {
    const savedApplicationId = formDataStorage.loadFormData('application_id');
    if (savedApplicationId && typeof savedApplicationId === 'string' && savedApplicationId.trim()) {
      setDynamicApplicationId(savedApplicationId);
    } else if (applicationId) {
      setDynamicApplicationId(applicationId);
    }
  }, [applicationId]);

  const loadConfirmationSnapshot = useCallback(() => {
    if (!formId) {
      setConfirmationSnapshot(null);
      return;
    }
    const snapshot = formDataStorage.loadFormData(`confirmation_snapshot_${formId}`);
    if (snapshot && (snapshot.data_url || snapshot.imageData)) {
      setConfirmationSnapshot(snapshot);
    } else {
      setConfirmationSnapshot(null);
    }
  }, [formId]);

  useEffect(() => {
    loadConfirmationSnapshot();
  }, [loadConfirmationSnapshot]);

  useEffect(() => {
    const handleSnapshotUpdate = (event) => {
      const { formId: eventFormId } = event.detail || {};
      if (!eventFormId || eventFormId !== formId) {
        return;
      }
      loadConfirmationSnapshot();
    };

    window.addEventListener('confirmationSnapshotUpdated', handleSnapshotUpdate);
    return () => {
      window.removeEventListener('confirmationSnapshotUpdated', handleSnapshotUpdate);
    };
  }, [formId, loadConfirmationSnapshot]);

  // 监听applicationIdUpdated事件
  useEffect(() => {
    const handleApplicationIdUpdate = (event) => {
      const { applicationId: newApplicationId } = event.detail;
      if (newApplicationId && typeof newApplicationId === 'string' && newApplicationId.trim()) {
        setDynamicApplicationId(newApplicationId);
        // 显示成功通知
        setNotification({
          type: 'success',
          message: `Application ID 已获取: ${newApplicationId}`,
          formId: 'application_id'
        });
      }
    };

    window.addEventListener('applicationIdUpdated', handleApplicationIdUpdate);
    
    return () => {
      window.removeEventListener('applicationIdUpdated', handleApplicationIdUpdate);
    };
  }, []);

  // 获取当前签证类型并动态计算表单页面（用 useCallback 稳定引用，避免 hooks 依赖循环）
  const getCurrentVisaType = useCallback(() => {
    const travelData = formDataStorage.loadFormData('travel');
    if (travelData?.purpose?.length > 0) {
      const firstPurpose = travelData.purpose[0];
      if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F1-')) {
        return 'F';
      } else if (firstPurpose.sub_type && firstPurpose.sub_type.startsWith('F2-')) {
        return 'F2';
      }
    }
    return null;
  }, []);

  // 更新动态表单页面（稳定引用）
  const updateDynamicFormPages = useCallback(() => {
    const visaType = getCurrentVisaType();
    const pages = getFormPagesForVisaType(visaType);
    setDynamicFormPages(pages);
  }, [getCurrentVisaType]);

  // 通过 ref 读取当前页，避免把 currentPage 放入依赖导致不必要的重新加载
  const currentPageRef = React.useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // 保存表单数据（稳定引用）
  const saveFormData = useCallback((key, value) => {
    formDataStorage.saveFormData(key, value);
  }, []);

  // 更新表单状态（稳定引用）
  const updateFormStatuses = useCallback(() => {
    const statuses = getAllFormStatuses();
    setFormStatuses(statuses);
    // 更新动态表单页面
    updateDynamicFormPages();
  }, [updateDynamicFormPages]);

  // 从后端加载表单数据并映射到本地存储（稳定引用）
  const loadFormDataFromBackend = useCallback(async (formId) => {
    try {
      setIsLoadingForm(true);
      // 清空session
      formDataStorage.clearAllFormData();
      setConfirmationSnapshot(null);
      // 向后端请求特定的表单数据
      const result = await getSpecificForm(formId);
      if (!result.success || result.data === null) {
        setNotification({
          type: 'error',
          message: result.error || '加载表单数据失败',
          formId: formId
        });
        throw new Error(result.error || '加载表单数据失败');
      }
      const backendData = result.data;
      // 🆕 添加详细的控制台输出
      console.log('🎯 ===== 数据库表单数据读取成功 =====');
      console.log('📋 表单ID:', formId);
      console.log('📊 从数据库读取到的完整数据:', backendData);
      

      const confirmationData = backendData.confirmation_snapshot || null;

      Object.entries(backendData).forEach(([key, value]) => {
        if (!key || value === undefined || value === null) {
          return;
        }

        if (key === 'confirmation_snapshot') {
          if (value && typeof value === 'object') {
            const normalizedSnapshot = {
              data_url: value.data_url || value.imageData || '',
              filename: value.filename || '',
              application_id: value.application_id || value.applicationId || '',
              created_at: value.created_at || value.savedAt || null,
              path: value.path || '',
              file_size: value.file_size || value.size || null,
            };

            formDataStorage.saveFormData(`confirmation_snapshot_${formId}`, normalizedSnapshot);
            setConfirmationSnapshot(normalizedSnapshot);
          }
          return;
        }

        saveFormData(key, value);
        if (value.is_completed) {
          formDataStorage.markPageVisited(key);
        }
      });

      if (confirmationData && (confirmationData.data_url || confirmationData.imageData)) {
        const normalizedSnapshot = {
          data_url: confirmationData.data_url || confirmationData.imageData || '',
          filename: confirmationData.filename || '',
          application_id: confirmationData.application_id || confirmationData.applicationId || '',
          created_at: confirmationData.created_at || confirmationData.savedAt || null,
          path: confirmationData.path || '',
          file_size: confirmationData.file_size || confirmationData.size || null,
        };
        setConfirmationSnapshot(normalizedSnapshot);
        formDataStorage.saveFormData(`confirmation_snapshot_${formId}`, normalizedSnapshot);
      } else if (!confirmationData) {
        setConfirmationSnapshot(formDataStorage.loadFormData(`confirmation_snapshot_${formId}`));
      }
      // 更新侧边栏状态以保证数据同步
      updateFormStatuses();
      
      // 在数据加载完成后，设置当前页面的formData
      const currentPageData = formDataStorage.loadFormData(currentPageRef.current);
      if (currentPageData && Object.keys(currentPageData).length > 0) {
        setFormData(currentPageData);
      }
    } catch (error) {
      console.error('❌ 加载表单数据时发生错误:', error);
      setNotification({
        type: 'error',
        message: '加载表单数据时发生错误',
        formId: formId
      });
    } finally {
      setIsLoadingForm(false);
      console.log("渲染后端传来的数据后session内的数据", formDataStorage.getAllFormData());
    }
  }, [updateFormStatuses, saveFormData]);

  useEffect(() => {
    const savedData = formDataStorage.loadFormData(currentPage);
    formDataStorage.markPageVisited(currentPage);
    if (savedData && Object.keys(savedData).length > 0) {
      setFormData(savedData);
    } else {
      setFormData({});
    }
    updateFormStatuses();
  }, [currentPage, updateFormStatuses]);

  useEffect(() => {
    if (formId) {
      loadFormDataFromBackend(formId);
    } else {
      // 如果没有formId，更新表单状态
      updateFormStatuses();
    }
  }, [formId, loadFormDataFromBackend, updateFormStatuses]);

  const handleDownloadConfirmationSnapshot = () => {
    if (!confirmationSnapshot) {
      return;
    }

    const dataUrl = confirmationSnapshot.data_url || confirmationSnapshot.imageData;
    if (!dataUrl) {
      return;
    }

    const imageData = dataUrl.startsWith('data:') ? dataUrl : `data:image/png;base64,${dataUrl}`;

    const link = document.createElement('a');
    link.href = imageData;
    const defaultName = `DS160_Confirmation_${confirmationSnapshot.application_id || confirmationSnapshot.applicationId || dynamicApplicationId || 'UNKNOWN'}_${new Date().toISOString().slice(0, 10)}.png`;
    link.download = confirmationSnapshot.filename || defaultName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 注意：updateFormStatuses 与 saveFormData 已上移并用 useCallback 稳定化

  // 更新表单字段（稳定引用，避免子组件 onChange 频繁变更导致的副作用重复触发）
  const updateFormField = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      saveFormData(currentPage, newData);
      // 进行输入校验
      updateFormStatuses();
      return newData;
    });
  }, [currentPage, saveFormData, updateFormStatuses]);

  // 切换页面
  const handlePageChange = async (pageId) => {
    // 保存当前页面的数据到临时存储
    if (formData && Object.keys(formData).length > 0) {
      saveFormData(currentPage, formData);
    }
    // 立即更新当前页面的表单状态
    updateFormStatuses();
    setCurrentPage(pageId);
  };

  // 下一页
  const handleNextPage = async () => {
    // 保存当前页面的数据到临时存储
    if (formData && Object.keys(formData).length > 0) {
      console.log(`💾 下一页时保存当前数据 ${currentPage}:`, formData);
      saveFormData(currentPage, formData);
    }

    // 立即更新当前页面的表单状态
    updateFormStatuses();

    // 本页校验：如不通过则阻断并滚动到错误提示
    try {
      const validator = formValidators[currentPage];
      if (validator) {
        const { isValid, errors, errorDetails } = validator(formDataStorage.loadFormData(currentPage) || {});
        if (!isValid) {
          setCurrentErrors(errors || []);
          setCurrentErrorDetails(errorDetails || []);
          setNotification({ type: 'error', message: (errors && errors[0]) || '请修正本页错误后再继续', formId: currentPage });
          // 优先滚动到首个具体字段
          try {
            const first = (errorDetails && errorDetails.find(e => e && e.anchor)) || null;
            if (first && first.anchor) {
              const el = document.querySelector(first.anchor);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // 短暂闪烁高亮
                el.classList.add('ring-2','ring-red-400');
                setTimeout(()=>{ try{ el.classList.remove('ring-2','ring-red-400'); }catch(e){} }, 1500);
              } else {
                formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            } else {
              formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } catch (e) {}
          return;
        }
      }
      // 校验通过后清空本页错误展示
      if (currentErrors.length) setCurrentErrors([]);
      if (currentErrorDetails.length) setCurrentErrorDetails([]);
    } catch (e) {
      // 校验异常不阻断页面流转，但给出提醒
      console.warn('本页校验异常：', e);
    }

    // 自动保存到数据库（不阻塞页面切换）
    try {
      console.log('🔄 下一页时自动保存到数据库...');
      const saveResult = await autoSaveCurrentFormData();
      if (saveResult.success) {
        console.log('✅ 下一页时数据库保存成功');
      } else {
        console.warn('⚠️ 下一页时数据库保存失败:', saveResult.error);
      }
    } catch (error) {
      console.error('❌ 下一页时数据库保存出错:', error);
    }

    const currentIndex = dynamicFormPages.findIndex(page => page.id === currentPage);

    // 特殊逻辑：如果当前是part5页面，检查是否有F1签证类型
    if (currentPage === 'part5') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (hasF1Visa) {
        // 如果有F1签证，跳转到F1额外联系人表单
        setCurrentPage('f1_additional_contacts');
        return;
      }
    }

    // 正常逻辑：如果当前是F1学校信息表单，检查是否应该跳过
    if (currentPage === 'f1_school_info') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (!hasF1Visa) {
        // 如果没有F1签证，跳过F1学校信息表单
        if (currentIndex < dynamicFormPages.length - 1) {
          setCurrentPage(dynamicFormPages[currentIndex + 1].id);
        }
        return;
      }
    }

    // 正常逻辑：如果当前是F1额外联系人表单，检查是否应该跳过
    if (currentPage === 'f1_additional_contacts') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (!hasF1Visa) {
        // 如果没有F1签证，跳过F1额外联系人表单
        if (currentIndex < dynamicFormPages.length - 1) {
          setCurrentPage(dynamicFormPages[currentIndex + 1].id);
        }
        return;
      }
    }

    // 特殊逻辑：如果当前是F1额外联系人表单，跳转到F1学校信息表单
    if (currentPage === 'f1_additional_contacts') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (hasF1Visa) {
        // 如果有F1签证，跳转到F1学校信息表单
        setCurrentPage('f1_school_info');
        return;
      }
    }

    // 正常页面导航
    if (currentIndex < dynamicFormPages.length - 1) {
      setCurrentPage(dynamicFormPages[currentIndex + 1].id);
    }
  };

  // 上一页
  const handlePrevPage = async () => {
    // 保存当前页面的数据到临时存储
    if (formData && Object.keys(formData).length > 0) {
      saveFormData(currentPage, formData);
    }

    // 自动保存到数据库（不阻塞页面切换）
    try {
      console.log('🔄 上一页时自动保存到数据库...');
      const saveResult = await autoSaveCurrentFormData();
      if (saveResult.success) {
        console.log('✅ 上一页时数据库保存成功');
      } else {
        console.warn('⚠️ 上一页时数据库保存失败:', saveResult.error);
      }
    } catch (error) {
      console.error('❌ 上一页时数据库保存出错:', error);
    }

    const currentIndex = dynamicFormPages.findIndex(page => page.id === currentPage);

    // 特殊逻辑：如果当前是F1额外联系人表单，跳转到part5
    if (currentPage === 'f1_additional_contacts') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (hasF1Visa) {
        // 如果有F1签证，跳转到part5
        setCurrentPage('part5');
        return;
      }
    }

    // 特殊逻辑：如果当前是F1学校信息表单，跳转到F1额外联系人表单
    if (currentPage === 'f1_school_info') {
      const travelData = formDataStorage.loadFormData('travel');
      const hasF1Visa = travelData?.purpose?.some(purpose =>
        purpose.sub_type === 'F1-F1'
      );

      if (hasF1Visa) {
        // 如果有F1签证，跳转到F1额外联系人表单
        setCurrentPage('f1_additional_contacts');
        return;
      }
    }

    // 正常页面导航
    if (currentIndex > 0) {
      setCurrentPage(dynamicFormPages[currentIndex - 1].id);
    }
  };



  // 渲染表单内容
  // Note: 这个函数用来渲染不同的表单页面
  const renderFormContent = () => {
    const currentPageIndex = dynamicFormPages.findIndex(page => page.id === currentPage);
    const FormComponent = formComponents[currentPage];
    if (!FormComponent) {
      return <div>表单组件未找到</div>;
    }
    // 将当前页的错误映射为 {anchor: message}
    const fieldErrors = (currentErrorDetails || []).reduce((acc, e) => {
      if (e && e.anchor) acc[e.anchor] = e.message || '';
      return acc;
    }, {});

    return (
      <div>
        <ProgressBar formStatuses={formStatuses} totalPages={dynamicFormPages.length} />

        {/* 错误提示（本页） */}
        {currentErrors && currentErrors.length > 0 && (
          <div ref={formTopRef} className="mb-4 p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            <div className="font-semibold mb-1">请先修正本页错误：</div>
            <ul className="list-disc pl-5 text-sm space-y-1">
              {(currentErrorDetails.length > 0 ? currentErrorDetails : currentErrors.map(m=>({message:m}))).slice(0,8).map((err, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        if (err.anchor) {
                          const el = document.querySelector(err.anchor);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            el.classList.add('ring-2','ring-red-400');
                            setTimeout(()=>{ try{ el.classList.remove('ring-2','ring-red-400'); }catch(e){} }, 1500);
                            return;
                          }
                        }
                        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } catch(e) {}
                    }}
                    className="text-left underline-offset-2 hover:underline"
                  >{err.message || err}</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`bg-white rounded-lg shadow-md p-6 ${currentErrors && currentErrors.length>0 ? 'border-2 border-red-400 ring-2 ring-red-200' : ''}`}>
          <FormInstructions />
          <FormComponent data={formData} onChange={updateFormField} fieldErrors={fieldErrors} />
        </div>

        <div className="flex justify-between items-center mt-8">
          <button
            onClick={handlePrevPage}
            disabled={currentPageIndex === 0}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            上一页
          </button>

          {/* 下一页按钮（移除右下角“提交申请”按钮，统一使用左侧侧边栏提交） */}
          <div>
            {(() => {
              // 检查是否是最后一页
              const isLastPage = currentPageIndex === dynamicFormPages.length - 1;

              // 特殊逻辑：如果当前是 F1 额外联系人表单且属于 F1 流程，原本这里会显示“提交申请”按钮。
              // 为避免重复入口并与左侧提交保持一致，这里不再渲染提交按钮。
              if (currentPage === 'f1_additional_contacts') {
                const travelData = formDataStorage.loadFormData('travel');
                const hasF1Visa = travelData?.purpose?.some(purpose => purpose.sub_type === 'F1-F1');
                if (hasF1Visa) {
                  return null; // 最后一页且为 F1 扩展页：不显示右下角提交按钮
                }
              }

              // 非最后一页：显示“下一页”按钮；最后一页：不显示右下角提交按钮
              return isLastPage ? null : (
                <button
                  onClick={handleNextPage}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  下一页
                </button>
              );
            })()}
          </div>
        </div>



        {/* 提交结果显示 */}
        {(() => {
          const isLastPage = currentPageIndex === dynamicFormPages.length - 1;
          const isF1LastPage = currentPage === 'f1_additional_contacts' && (() => {
            const travelData = formDataStorage.loadFormData('travel');
            return travelData?.purpose?.some(purpose => purpose.sub_type === 'F1-F1');
          })();

          return (isLastPage || isF1LastPage) && submissionResult && (
            <div className={`mt-4 p-3 rounded-lg border ${submissionResult.success
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
              }`}>
              <div className="text-sm font-medium">
                {submissionResult.success ? '✅ ' : '❌ '}
                {submissionResult.message}
                {submissionResult.success && submissionResult.data?.debug_source && (
                  <div className="mt-1 text-xs opacity-80">
                    数据源: {submissionResult.data.debug_source}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* 提交说明 */}
        {(() => {
          const isLastPage = currentPageIndex === dynamicFormPages.length - 1;
          const isF1LastPage = currentPage === 'f1_additional_contacts' && (() => {
            const travelData = formDataStorage.loadFormData('travel');
            return travelData?.purpose?.some(purpose => purpose.sub_type === 'F1-F1');
          })();

          return (isLastPage || isF1LastPage) && (
            <div className="mt-2 text-xs text-gray-500 text-center">
              <div>
                {canSubmit()
                  ? '✅ 所有必填项已完成，可以提交'
                  : '⚠️ 请先完成所有必填项再提交'
                }
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Sidebar
        currentPageId={currentPage}
        onPageChange={handlePageChange}
        formStatuses={formStatuses}
        currentFormData={formData}
        onNotification={setNotification}
      />

      <div className="ml-64">
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/home')}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <Home className="w-5 h-5 mr-2" />
                返回首页
              </button>
            </div>
            <h1 className="text-xl font-semibold text-gray-800">
              {dynamicFormPages.find(page => page.id === currentPage)?.title}
            </h1>
            <div className="relative">
              <UserCenter
                applicationId={dynamicApplicationId}
                onLogout={onLogout}
                onHistoryClick={onHistoryClick}
                isInHeader={true}
              />
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* 加载状态 */}
          {isLoadingForm && (
            <div className="mb-4 p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-700">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <div className="font-medium">正在加载表单数据...</div>
              </div>
            </div>
          )}

          {/* 通知消息 */}
          {notification && (
            <div className={`mb-4 p-4 rounded-lg border ${notification.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
              }`}>
              <div className="flex items-center">
                {notification.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-2" />
                )}
                <div className="flex-1">
                  <div className="font-medium">{notification.message}</div>
                  {notification.formId && (
                    <div className="text-sm opacity-80 mt-1">
                      表单ID: {notification.formId}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setNotification(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {confirmationSnapshot && (confirmationSnapshot.data_url || confirmationSnapshot.imageData) && (
            <div className="mb-4 p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-700 flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-3 md:mb-0">
                <div className="font-medium">确认页截图已生成并保存</div>
                {(confirmationSnapshot.application_id || confirmationSnapshot.applicationId || dynamicApplicationId) && (
                  <div className="text-sm opacity-80 mt-1">
                    Application ID: {confirmationSnapshot.application_id || confirmationSnapshot.applicationId || dynamicApplicationId || '未知'}
                  </div>
                )}
                {confirmationSnapshot.created_at && (
                  <div className="text-xs opacity-70 mt-1">生成时间：{new Date(confirmationSnapshot.created_at).toLocaleString()}</div>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDownloadConfirmationSnapshot}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  下载确认页截图
                </button>
              </div>
            </div>
          )}

          {renderFormContent()}
        </main>
      </div>
    </div>
  );
};

export default FormBuilder;
